import { useState } from "react";
import { MapPin, Search, CheckCircle2, AlertCircle, MessageCircle, ArrowRight } from "lucide-react";
import { SEO } from "@/lib/seo";
import { waLink, DEFAULT_WA_MESSAGE } from "@/lib/whatsapp";
import { Reveal } from "@/components/Reveal";

const AREAS = [
  "El Poblado", "Laureles", "Estadio", "Belén", "La Candelaria", "Robledo",
  "Castilla", "Buenos Aires", "San Javier", "La América", "Aranjuez", "Manrique",
  "Popular", "Santa Cruz", "Doce de Octubre", "Envigado", "Sabaneta", "Itagüí",
  "La Estrella", "Caldas", "Bello", "Copacabana", "Girardota", "Barbosa", "Rionegro",
];

const norm = (s) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

const CoveragePage = () => {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);

  const checkArea = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    const q = norm(query);
    const found = AREAS.find((a) => norm(a).includes(q) || q.includes(norm(a)));
    setResult(found ? { ok: true, area: found } : { ok: false });
  };

  return (
    <div data-testid="coverage-page">
      <SEO
        title="Cobertura en Medellín y Valle de Aburrá | Colors Limpieza"
        description="Llegamos a domicilio en El Poblado, Laureles, Envigado, Sabaneta, Belén, Itagüí, Rionegro y todo el Valle de Aburrá. Verifica tu zona."
      />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="coverage-hero">
        <img
          src="https://images.unsplash.com/photo-1709152213008-71edae302aad?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Panorámica de Medellín"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-12">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur">
              <MapPin size={14} /> Servicio a domicilio sin costo adicional
            </div>
            <h1 className="mt-5 max-w-2xl font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
              Cubrimos Medellín y todo el Valle de Aburrá
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-200">
              Nuestro equipo llega hasta tu casa, oficina o parqueadero con todos los equipos e insumos necesarios.
            </p>
          </Reveal>
        </div>
      </section>

      {/* VERIFICADOR DE ZONA */}
      <section className="bg-white py-16 md:py-24" data-testid="coverage-checker-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <Reveal>
            <div className="rounded-3xl border border-slate-100 bg-slate-50 p-6 text-center md:p-10">
              <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                ¿Llegamos a tu sector?
              </h2>
              <p className="mt-3 text-sm text-slate-600">Escribe tu barrio o municipio y verifícalo al instante.</p>
              <form onSubmit={checkArea} className="mx-auto mt-6 flex max-w-md gap-2">
                <input
                  type="text"
                  placeholder="Ej: Envigado, Laureles, Sabaneta..."
                  className="w-full rounded-full border border-slate-200 bg-white px-5 py-3.5 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/20"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setResult(null); }}
                  data-testid="coverage-checker-input"
                />
                <button
                  type="submit"
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-colors hover:bg-brand-dark"
                  aria-label="Verificar zona"
                  data-testid="coverage-checker-button"
                >
                  <Search size={18} />
                </button>
              </form>
              {result && (
                <div
                  className={`mx-auto mt-6 max-w-md rounded-2xl p-5 text-left ${
                    result.ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
                  }`}
                  data-testid="coverage-checker-result"
                >
                  {result.ok ? (
                    <div className="flex items-start gap-3">
                      <CheckCircle2 size={22} className="mt-0.5 shrink-0 text-emerald-600" />
                      <div>
                        <p className="font-display text-sm font-bold">¡Sí! Cubrimos {result.area}</p>
                        <p className="mt-1 text-sm">Agenda tu servicio sin costo de desplazamiento.</p>
                        <a
                          href={waLink(`¡Hola Colors Limpieza! Estoy en ${result.area} y quiero cotizar un servicio.`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-bold text-white hover:bg-[#1EBE5A]"
                          data-testid="coverage-checker-whatsapp"
                        >
                          <MessageCircle size={15} /> Cotizar ahora
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <AlertCircle size={22} className="mt-0.5 shrink-0 text-amber-600" />
                      <div>
                        <p className="font-display text-sm font-bold">No encontramos "{query}" en nuestra lista</p>
                        <p className="mt-1 text-sm">Es posible que igual lleguemos a tu zona. Escríbenos para confirmar.</p>
                        <a
                          href={waLink(`¡Hola Colors Limpieza! Estoy en ${query} y quiero saber si cubren mi zona.`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-bold text-white hover:bg-[#1EBE5A]"
                          data-testid="coverage-checker-whatsapp"
                        >
                          <MessageCircle size={15} /> Confirmar mi zona
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ZONAS */}
      <section className="bg-slate-50 py-16 md:py-24" data-testid="coverage-areas-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Zonas de cobertura
            </h2>
            <p className="mt-3 max-w-2xl text-base text-slate-600">
              Estas son las zonas donde atendemos a domicilio sin costo adicional de desplazamiento.
            </p>
          </Reveal>
          <div className="mt-10 flex flex-wrap gap-3">
            {AREAS.map((a, i) => (
              <Reveal key={a} delay={Math.min(i * 0.03, 0.4)}>
                <span
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-brand hover:text-brand-dark"
                  data-testid={`coverage-area-${norm(a).replace(/\s+/g, "-")}`}
                >
                  <MapPin size={14} className="text-brand" /> {a}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 rounded-3xl bg-gradient-to-br from-brand to-brand-dark p-8 md:p-12">
              <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
                <div>
                  <h3 className="font-display text-xl font-bold text-white sm:text-2xl">
                    ¿Tu zona no está en la lista?
                  </h3>
                  <p className="mt-2 max-w-lg text-sm leading-relaxed text-white/85">
                    Ampliamos cobertura constantemente. Escríbenos y buscamos la forma de llegar hasta ti.
                  </p>
                </div>
                <a
                  href={waLink(DEFAULT_WA_MESSAGE)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-bold text-brand-dark transition-transform hover:scale-105"
                  data-testid="coverage-cta-whatsapp"
                >
                  Preguntar por mi zona <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default CoveragePage;
